<template>
    <div class="add-to-cart-popover" :class="{ fullscreen: isFullscreen }">
        <div class="closer" @click="closePopover">
            <i class="fal fa-times"></i>
        </div>

        <header class="articleinfo">
            <img v-if="showItemImages && item.imagePath" class="image" :src="item.imagePath" alt="" />
            <div class="articlename">{{ articleName }}<ItemIcons :articleId="item.articleId" includeOrderedBefore /></div>
            <div class="price" v-if="!props.item.isOpenPriceEnabled">{{ toMoneyWithSign(unitPrice) }}</div>
            <div class="shortdescription" v-if="shortDescription">
                {{ shortDescription }}
            </div>
        </header>

        <ScrollableView>
            <div class="longdescription" v-if="longDescription">
                {{ longDescription }}
            </div>

            <ArticleAttributeList :attributes="articleAttribute.filterAttributesForArticle(item.articleId)" />

            <ArticleOptionsList
                ref="articleOptionsList"
                :item="item"
                :offerUseCaseId="offer.orderService!.useCase!.id"
                :locationId="allocation.locationId"
                :offerId="offer.offer?.offerId"
                :showInternalNames="true"
                :showOohPrices="showOohPrices"
                :showItemImages="showItemImages"
                :offerOptionGroups="offer.offer?.offerOptionGroups"
                @selectionChanged="onOptionsSelectionChanged"
            />
        </ScrollableView>

        <footer :class="{ 'no-quantity': !hasVisibleQuanityInput }">
            <div class="open-price-input-container" v-if="item.isOpenPriceEnabled">
                <label for="open-price-input">{{ trans_vue.addToCartPopover.price }}</label>
                <CurrencyInput v-model="openPrice" :id="'open-price-input'" :min="minOpenPrice" :max="maxOpenPrice" />
            </div>

            <QuantityInput
                :min="minQuantity"
                :max="maxQuantity"
                :disabled="cart.isUpdating"
                v-model:quantity="quantityToAdd"
                v-if="hasVisibleQuanityInput"
            />

            <ProceedButton
                class="add-to-cart-button"
                :currentPrice="totalAmount"
                :disabled="!isAddToCartButtonActive"
                @click="onAddToCartClick"
            >
                <span v-if="!cart.isUpdating">{{ translations.general.add }}</span>
                <span v-else>{{ trans_vue.addToCartPopover.adding }}</span>
            </ProceedButton>
        </footer>
    </div>
    <div class="add-to-cart-popover-overlay" @click="closePopover"></div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { useAddToCart } from "@/components/order/useAddToCart";
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import type { ICustomerArticleWithOptions } from "@/data/model/Customer";
    import { ConsumptionSite } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { useOfferStore } from "@/data/stores/offer";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { getSitePrice } from "@/shared/helpers/ItemPriceHelper";
    import { translate, translateByLocationLanguage } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, ref, watch } from "vue";

    import CurrencyInput from "@/components/elements/CurrencyInput.vue";
    import ProceedButton from "@/components/elements/ProceedButton.vue";
    import QuantityInput from "@/components/elements/QuantityInput.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import ArticleAttributeList from "@/components/order/ArticleAttributeList.vue";
    import ArticleOptionsList from "@/components/order/ArticleOptionsList.vue";
    import ItemIcons from "@/components/order/ItemIcons.vue";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";

    // define stores
    const cart = useCartStore();
    const offer = useOfferStore();
    const allocation = useTableAllocationStore();
    const articleAttribute = useArticleAttributesStore();

    // define properties
    const props = defineProps<{
        item: ICustomerArticleWithOptions;
    }>();

    // define emmitted events
    const emit = defineEmits(["closed", "itemAdded"]);

    // use add to cart functionality
    const {
        quantityToAdd,
        openPrice,
        selectedOptions,
        minQuantity,
        maxQuantity,
        isAddToCartAvailable,
        addItemToCart,
        fixCurrentQuantity,
    } = useAddToCart(props.item, offer.orderService);

    // define computed
    const unitPrice = computed(() => {
        return props.item.isOpenPriceEnabled ? (openPrice.value ?? 0) : getSitePrice(props.item, offer.orderService);
    });

    const totalAmount = computed(() => {
        let unitPriceWithOptions = unitPrice.value;
        unitPriceWithOptions += articleOptionsList.value?.totalPrice ?? 0;

        const totalPrice = unitPriceWithOptions * quantityToAdd.value;
        return totalPrice;
    });

    const showOohPrices = computed(() => {
        const orderApplication = offer.orderService?.useCase;
        const site = orderApplication == null ? ConsumptionSite.OnSite : orderApplication.consumptionSite;

        return site === ConsumptionSite.OffSite;
    });
    const showItemImages = computed(() => offer.orderService?.useCase?.operatorDisplayItemImages === true);
    const minOpenPrice = computed(() => {
        return props.item.isNegativeOpenPrice ? -20 : 0;
    });
    const maxOpenPrice = computed(() => {
        return props.item.isNegativeOpenPrice ? 0 : 9_999_999;
    });
    const articleName = computed(() => getNameInLocationLanguage(props.item, true));
    const shortDescription = computed(() => translateByLocationLanguage(props.item.shortDescription));
    const longDescription = computed(() => translateByLocationLanguage(props.item.longDescription));
    const shouldWarnBeforeAdding = computed(() => cart.hasItems && cart.orderService?.id != offer.orderService?.id);

    const areOptionsLoaded = computed(() => {
        if (articleOptionsList.value != null) {
            return articleOptionsList.value.isLoading == false;
        }
        return false;
    });
    const isAddToCartButtonActive = computed(() => areOptionsLoaded.value && isAddToCartAvailable.value);

    const isFullscreen = computed(() => props.item.hasOptions);
    const hasVisibleQuanityInput = computed(() => maxQuantity.value != 1);

    // define template refs
    const articleOptionsList = ref<InstanceType<typeof ArticleOptionsList> | null>(null);

    // define methods
    async function onAddToCartClick() {
        if (shouldWarnBeforeAdding.value) {
            const warningMessage = trans_vue.cart.haveItemsInCart.replace(
                "{0}",
                "<b>" + translate(cart.orderService?.useCase?.name) + "</b>"
            );
            Popup.show({
                uniqueId: "items-in-cart",
                message: warningMessage,
                confirmButton: translations.general.continue,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: false,
                confirmHandler: async () => {
                    await cart.deleteCartsFromLocation();
                    await addToCart();
                },
            });
        } else {
            await addToCart();
        }
    }

    function onOptionsSelectionChanged(options: IArticleWithOptions[]) {
        selectedOptions.value = options;
    }

    async function addToCart() {
        const options = getAllItemOptions();
        selectedOptions.value = options;

        try {
            await addItemToCart();
            closePopover();
            emit("itemAdded");
        } catch (ex) {
            logError(ex);
            Popup.show({ message: trans_vue.cart.failedToAdd });
        }
    }

    function getAllItemOptions(): IArticleWithOptions[] {
        const options = articleOptionsList.value?.getSelectedArticlesWithOptions() ?? [];
        return options;
    }

    function closePopover() {
        emit("closed");
    }

    // define watchers
    watch(
        () => props.item,
        () => {
            quantityToAdd.value = 1;
            openPrice.value = null;
            fixCurrentQuantity();
        }
    );
    watch(minQuantity, fixCurrentQuantity);
    watch(maxQuantity, fixCurrentQuantity);
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .add-to-cart-popover {
        position: relative;
        background: white;
        padding-top: 62px;
        border-top: 1px solid #ccc;
        z-index: 2;
        border-top-right-radius: 30px;
        border-top-left-radius: 30px;

        display: flex;
        flex-direction: column;

        &.fullscreen {
            border-top: none;
            height: 100%;
        }

        &:not(.fullscreen) {
            .scrollable-view {
                flex: 0 0 auto;
            }

            .scrollable-content {
                position: static;
            }
        }

        > .closer {
            position: absolute;
            right: 0;
            top: 0;
            width: 62px;
            cursor: pointer;
            text-align: center;

            > .fal {
                line-height: 70px;
                color: colors.$fontColor;
                font-size: 30px;
            }

            @include mixins.hover-and-active() {
                > .fal {
                    color: colors.$lightFontColor;
                }
            }
        }

        > .articleinfo {
            flex: 0 0 auto;

            position: relative;
            // image | name | price on the first row, the short description on a second
            // row next to the image, aligned with the name. a wrapped flex line would
            // always start below the image, so the image spans both rows instead
            display: grid;
            grid-template-columns: auto 1fr auto;
            align-items: start;
            padding: 0 25px;
            margin-bottom: 10px;

            > .image {
                width: 40px;
                height: 40px;
                object-fit: cover;
                border-radius: 4px;
                grid-area: 1 / 1 / span 2;
                margin-right: 10px;
            }

            > .articlename {
                font-size: 18px;
                font-weight: 400;
                color: colors.$fontColor;
                line-height: 22px;
                grid-area: 1 / 2;
            }

            > .price {
                font-size: 16px;
                font-weight: 300;
                color: colors.$fontColor;
                line-height: 22px;
                grid-area: 1 / 3;
            }

            > .shortdescription {
                // runs under the name and on into the price column
                grid-area: 2 / 2 / auto / -1;
                min-width: 0;
                font-size: 12px;
                font-weight: 300;
                color: #aaaaaa;
                line-height: 18px;
                padding: 0 0 11px 0;
                margin-top: 0.25em;
            }
        }

        > .scrollable-view {
            flex: 1 1 auto;

            > .scrollable-content {
                padding: 0 25px;

                > .longdescription {
                    font-size: 12px;
                    font-weight: 300;
                    color: colors.$fontColor;
                    line-height: 18px;
                    padding: 0 0 11px 0;
                }
            }
        }

        > footer {
            flex: 0 0 auto;
            padding: 0 15px 15px;

            &.no-quantity {
                padding-top: 59px;
            }

            > .open-price-input-container {
                padding: 1em 1em 1.5em;

                label {
                    font-weight: 600;
                    font-size: 14px;
                    margin-right: 2em;
                }

                input {
                    width: 280px;
                }
            }

            > .quantity-input {
                margin: 0 auto 21px auto;

                > .circle-button {
                    background-color: colors.$green;
                    // background-color: var(--colorPrimary);

                    &:disabled {
                        cursor: default;
                    }

                    @include mixins.hover-and-active() {
                        background-color: colors.$greenHover;
                        // background-color: var(--colorPrimaryHover);
                    }
                }
            }
        }
    }

    .add-to-cart-popover-overlay {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;

        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1;
    }
</style>
